/**
 * Step 4: Generate & Download
 *
 * Final wizard step that generates the C10 file, displays a summary,
 * and provides download and filing management options.
 *
 * Session 4.3: Step 4 - Generate & Download
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DocumentArrowDownIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { useWizard } from '../../context/WizardContext';
import { useBusinessProfile } from '@/hooks/business/useBusinessProfile';
import {
  generationApi,
  type GenerateC10Response,
} from '@/services/api/generationApi';

// =============================================================================
// CONSTANTS
// =============================================================================

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Format currency for display.
 */
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Step 4: Generate C10 and download.
 */
export function Step4Generate() {
  const navigate = useNavigate();
  const { state, setGeneratedFile, setError, reset } = useWizard();
  const { data: businessProfile } = useBusinessProfile();

  // Local state
  const [generateResult, setGenerateResult] = useState<
    GenerateC10Response['data'] | null
  >(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isMarkingFiled, setIsMarkingFiled] = useState(false);
  const [markedAsFiled, setMarkedAsFiled] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  // Generate C10 file
  const generateC10 = useCallback(async () => {
    if (!businessProfile?.employer_ni_number) {
      setLocalError('Business profile not loaded');
      return;
    }

    setIsGenerating(true);
    setLocalError(null);
    setError(null);

    try {
      const response = await generationApi.generateC10({
        employer_ni: businessProfile.employer_ni_number,
        contribution_month: state.contributionMonth,
        contribution_year: state.contributionYear,
        format_type: state.formatType,
        employees: state.employees.map(emp => ({
          employee_ni: emp.employeeNi,
          last_name: emp.lastName,
          first_name: emp.firstName,
          middle_initial: emp.middleInitial,
          basic_wages: emp.basicWages,
          gratuity_wages: emp.gratuityWages,
          weeks_worked: emp.weeksWorked,
          pay_indicator: emp.payIndicator,
          category: emp.category,
        })),
      });

      setGenerateResult(response.data);
      setGeneratedFile({
        url: response.data.file_url,
        filename: response.data.filename,
        generatedAt: new Date(),
        employeeCount: response.data.summary.employee_count,
        totalContributions: response.data.summary.total_contributions,
        filingId: response.data.filing_id,
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to generate C10';
      setLocalError(message);
      setError(message);
    } finally {
      setIsGenerating(false);
    }
  }, [
    businessProfile,
    state.contributionMonth,
    state.contributionYear,
    state.formatType,
    state.employees,
    setGeneratedFile,
    setError,
  ]);

  // Generate on mount
  useEffect(() => {
    if (!generateResult && !isGenerating && businessProfile) {
      generateC10();
    }
  }, [generateResult, isGenerating, businessProfile, generateC10]);

  // Handle file download
  const handleDownload = async () => {
    if (!generateResult) return;

    setIsDownloading(true);
    try {
      const blob = await generationApi.downloadC10(generateResult.file_url);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = generateResult.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      setLocalError('Failed to download file');
    } finally {
      setIsDownloading(false);
    }
  };

  // Handle mark as filed
  const handleMarkAsFiled = async () => {
    if (!generateResult) return;

    setIsMarkingFiled(true);
    try {
      await generationApi.markAsFiled(generateResult.filing_id);
      setMarkedAsFiled(true);
    } catch {
      setLocalError('Failed to mark as filed');
    } finally {
      setIsMarkingFiled(false);
    }
  };

  // Start new submission
  const handleStartNew = () => {
    reset();
    navigate('/converter/wizard');
  };

  // View filing history
  const handleViewHistory = () => {
    navigate('/filings');
  };

  // ==========================================================================
  // RENDER: Loading State
  // ==========================================================================

  if (isGenerating) {
    return (
      <div className="text-center py-12">
        <ArrowPathIcon className="mx-auto h-12 w-12 text-blue-600 animate-spin" />
        <h2 className="mt-4 text-lg font-medium text-gray-900">
          Generating C10 File...
        </h2>
        <p className="mt-2 text-sm text-gray-500">
          This may take a few seconds.
        </p>
      </div>
    );
  }

  // ==========================================================================
  // RENDER: Error State
  // ==========================================================================

  if (localError && !generateResult) {
    return (
      <div className="text-center py-12">
        <ExclamationCircleIcon className="mx-auto h-12 w-12 text-red-500" />
        <h2 className="mt-4 text-lg font-medium text-gray-900">
          Generation Failed
        </h2>
        <p className="mt-2 text-sm text-red-600">{localError}</p>
        <button
          onClick={generateC10}
          type="button"
          className="mt-4 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Try Again
        </button>
      </div>
    );
  }

  // ==========================================================================
  // RENDER: Success State
  // ==========================================================================

  return (
    <div>
      {/* Success header */}
      <div className="text-center mb-8">
        <CheckCircleIcon className="mx-auto h-16 w-16 text-green-500" />
        <h2 className="mt-4 text-xl font-medium text-gray-900">
          C10 Generated Successfully
        </h2>
      </div>

      {/* File download card */}
      <div className="bg-gray-50 rounded-lg p-6 mb-6 text-center">
        <div className="text-4xl mb-4">
          <span role="img" aria-label="Document">
            {'\uD83D\uDCC4'}
          </span>
        </div>
        <p className="font-mono text-sm text-gray-600 mb-4">
          {generateResult?.filename}
        </p>
        <button
          onClick={handleDownload}
          disabled={isDownloading}
          type="button"
          className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          {isDownloading ? (
            <>
              <ArrowPathIcon className="h-5 w-5 animate-spin" />
              Downloading...
            </>
          ) : (
            <>
              <DocumentArrowDownIcon className="h-5 w-5" />
              Download C10 File
            </>
          )}
        </button>
      </div>

      {/* Summary card */}
      {generateResult && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <h3 className="text-sm font-medium text-gray-900 mb-4 uppercase tracking-wider">
            Submission Summary
          </h3>

          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-gray-500">Period:</dt>
              <dd className="text-gray-900">
                {MONTHS[state.contributionMonth - 1]} {state.contributionYear}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Format:</dt>
              <dd className="text-gray-900">
                {state.formatType === 'HOSPITALITY'
                  ? 'Hospitality (115 bytes)'
                  : 'Standard (79 bytes)'}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Employees:</dt>
              <dd className="text-gray-900">
                {generateResult.summary.employee_count}
              </dd>
            </div>

            <div className="border-t border-gray-200 pt-2 mt-2">
              <div className="flex justify-between">
                <dt className="text-gray-500">Total Wages:</dt>
                <dd className="text-gray-900">
                  {formatCurrency(generateResult.summary.total_wages)}
                </dd>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-2 mt-2">
              <div className="flex justify-between font-medium">
                <dt className="text-gray-700">Total Contributions:</dt>
                <dd className="text-gray-900">
                  {formatCurrency(generateResult.summary.total_contributions)}
                </dd>
              </div>
              <div className="flex justify-between text-xs mt-1">
                <dt className="text-gray-400">Employee portion:</dt>
                <dd className="text-gray-500">
                  {formatCurrency(generateResult.summary.employee_portion)}
                </dd>
              </div>
              <div className="flex justify-between text-xs">
                <dt className="text-gray-400">Employer portion:</dt>
                <dd className="text-gray-500">
                  {formatCurrency(generateResult.summary.employer_portion)}
                </dd>
              </div>
              {generateResult.summary.gratuity_portion > 0 && (
                <div className="flex justify-between text-xs">
                  <dt className="text-gray-400">Gratuity:</dt>
                  <dd className="text-gray-500">
                    {formatCurrency(generateResult.summary.gratuity_portion)}
                  </dd>
                </div>
              )}
            </div>
          </dl>
        </div>
      )}

      {/* What's next card */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
        <h3 className="text-sm font-medium text-blue-900 mb-3 uppercase tracking-wider">
          What&apos;s Next?
        </h3>
        <ol className="list-decimal list-inside space-y-2 text-sm text-blue-800">
          <li>Download the C10 file above</li>
          <li>Submit to NIB via their portal or email</li>
          <li>Mark as filed in your Filing History</li>
        </ol>
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-gray-200">
        <div className="flex gap-3">
          <button
            onClick={handleMarkAsFiled}
            disabled={isMarkingFiled || markedAsFiled}
            type="button"
            className={`px-4 py-2 text-sm font-medium rounded-md ${
              markedAsFiled
                ? 'bg-green-100 text-green-800'
                : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
            } disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
          >
            {markedAsFiled
              ? '\u2713 Marked as Filed'
              : isMarkingFiled
                ? 'Marking...'
                : 'Mark as Filed'}
          </button>

          <button
            onClick={handleViewHistory}
            type="button"
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            View Filing History
          </button>
        </div>

        <button
          onClick={handleStartNew}
          type="button"
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Start New Submission
        </button>
      </div>

      {/* Error toast */}
      {localError && generateResult && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{localError}</p>
        </div>
      )}
    </div>
  );
}
